import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Microphone, Stop, SpeakerHigh, Robot, Waveform } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GAMES = ['Catan', 'Chess', 'D&D', 'Magic: The Gathering', 'Pokemon'];

export default function VoiceAssistantPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedGame, setSelectedGame] = useState(searchParams.get('game') || 'Catan');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [conversations, setConversations] = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        processAudio();
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async () => {
    setIsProcessing(true);
    try {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');

      const { data } = await axios.post(
        `${API_URL}/api/voice/ask?game_name=${selectedGame}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const conv = {
        question: data.question,
        answer: data.answer,
        audio: data.audio_base64 ? `data:audio/mpeg;base64,${data.audio_base64}` : null,
        timestamp: new Date().toLocaleTimeString()
      };
      setConversations(prev => [conv, ...prev]);

      // Auto-play response
      if (conv.audio) playAudio(conv.audio);

    } catch (err) {
      toast.error('Voice processing failed');
    } finally { setIsProcessing(false); }
  };

  const playAudio = (src) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(src);
    audioRef.current = audio;
    setIsPlaying(true);
    audio.play();
    audio.onended = () => setIsPlaying(false);
  };

  return (
    <div className="app-container" data-testid="voice-assistant-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Waveform size={24} weight="duotone" className="text-[#EF4444]" />
              Voice Assistant
            </h1>
            <p className="text-[#71717A] text-xs">Ask rules by voice</p>
          </div>
        </div>
        <div className="horizontal-scroll mb-3">
          {GAMES.map(g => <GamePill key={g} game={g} selected={selectedGame === g} onClick={() => setSelectedGame(g)} />)}
        </div>
      </header>

      <div className="px-6 pb-40 flex-1">
        {/* Voice Control */}
        <div className="text-center mb-6">
          <motion.button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isProcessing}
            animate={isRecording ? { scale: [1, 1.1, 1] } : {}}
            transition={isRecording ? { repeat: Infinity, duration: 1 } : {}}
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
              isRecording ? 'bg-[#EF4444] shadow-lg shadow-[#EF4444]/30' :
              isProcessing ? 'bg-[#181824] cursor-wait' :
              'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] hover:shadow-lg hover:shadow-[#9D4CDD]/30'
            }`}
            data-testid="voice-record-btn">
            {isRecording ? <Stop size={32} weight="fill" className="text-white" /> :
             isProcessing ? <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" /> :
             <Microphone size={32} weight="fill" className="text-white" />}
          </motion.button>
          <p className="text-[#71717A] text-xs mt-2">
            {isRecording ? 'Listening... Tap to stop' :
             isProcessing ? 'Processing...' :
             `Tap to ask about ${selectedGame}`}
          </p>
        </div>

        {/* Conversations */}
        <div className="space-y-3">
          <AnimatePresence>
            {conversations.map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                <p className="text-[#9D4CDD] text-xs font-medium mb-1">You asked:</p>
                <p className="text-white text-sm mb-3">{c.question}</p>
                <p className="text-[#00F0FF] text-xs font-medium mb-1">AI Answer:</p>
                <p className="text-[#A1A1AA] text-xs leading-relaxed mb-2">{c.answer}</p>
                {c.audio && (
                  <button onClick={() => playAudio(c.audio)}
                    className="flex items-center gap-1 text-[#9D4CDD] text-[10px]">
                    <SpeakerHigh size={14} weight={isPlaying ? 'fill' : 'regular'} />
                    Play Response
                  </button>
                )}
                <p className="text-[#71717A] text-[10px] mt-1">{c.timestamp}</p>
              </motion.div>
            ))}
          </AnimatePresence>

          {conversations.length === 0 && !isRecording && !isProcessing && (
            <div className="text-center py-6">
              <Robot size={48} weight="duotone" className="text-[#9D4CDD]/30 mx-auto mb-2" />
              <p className="text-[#71717A] text-sm">Ask a question about {selectedGame} using your voice</p>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
